import { DialogProps, Dialog } from '../../Dialog';
import { createElement, useState, useEffect } from 'react';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { DualSelector, Item as DualSelectorItem } from '../../DualSelector';
import { TableColumnProps } from '../';
import { TestIdProps } from '../../../util/testId';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';

export interface ManageColumnsDialogProps extends Pick<DialogProps, 'isOpen'>, TestIdProps {
  columns: ReadonlyArray<TableColumnProps>;
  orderedColumnKeys?: ReadonlyArray<string>;
  onDismiss?: () => void;
  onConfirm?: (orderedColumnKeys: ReadonlyArray<string>) => void;
  onClose?: () => void;
  onFocusDeactivation?: () => void;
}

export const ManageColumnsDialog = (props: ManageColumnsDialogProps) => {
  const localize = useNCTextLocalizer('Table');
  const [orderedColumnKeys, setOrderedColumnKeys] = useState<ReadonlyArray<string>>([]);

  useEffect(() => {
    if (!props.isOpen) {
      setOrderedColumnKeys(props.orderedColumnKeys ?? []);
    }
  }, [props.orderedColumnKeys, props.isOpen]);

  const iconInfo = useIconA11yInfo();
  const getDualSelectorItemByColumnKey = (columnKey: string): DualSelectorItem => {
    const column = props.columns.find(({ key }) => key === columnKey);
    let columnLabel = column?.label;
    if (column && !columnLabel && column.icon) {
      columnLabel = iconInfo({ icon: column.icon }).ariaLabel;
    }
    return {
      icon: column?.icon,
      columnLabel,
      columnKey,
    };
  };

  const displayedColumns: Array<DualSelectorItem> = orderedColumnKeys.map(
    getDualSelectorItemByColumnKey
  );

  const hiddenColumns: Array<DualSelectorItem> = props.columns
    .filter(({ key }) => !orderedColumnKeys.includes(key))
    .map(({ key }) => getDualSelectorItemByColumnKey(key));

  return (
    <Dialog
      isOpen={props.isOpen}
      title={localize('manageColumnsDialog.title')}
      firstAction={{
        text: localize('manageColumnsDialog.confirmButton.label'),
        onPress: () => props.onConfirm?.(orderedColumnKeys),
      }}
      dismissAction={{
        text: localize('manageColumnsDialog.dismissButton.label'),
        onPress: () => {
          setOrderedColumnKeys(props.orderedColumnKeys ?? []);
          props.onDismiss?.();
        },
      }}
      onClose={props.onClose}
      focusLockProps={{ onDeactivation: props.onFocusDeactivation }}
      size={{
        width: '48rem',
        height: '40rem',
        maxWidth: 'calc(100% - 4rem)',
        maxHeight: 'calc(100% - 4rem)',
      }}
      testId={props.testId}
    >
      <DualSelector
        style={{ height: '100%' }}
        type="list"
        mode="move"
        dataFieldKeys={['columnLabel']}
        sourceAttribute={[
          {
            label: localize('manageColumnsDialog.hiddenColumns.label'),
            data: hiddenColumns,
          },
        ]}
        targetAttribute={[
          {
            label: localize('manageColumnsDialog.displayedColumns.label'),
            data: displayedColumns,
          },
        ]}
        onTargetChange={(selectedItems) =>
          setOrderedColumnKeys(selectedItems.map(({ columnKey }) => columnKey))
        }
        testId={props.testId}
      />
    </Dialog>
  );
};
